import { useEffect, useState } from "react";
import type { BlogHeading } from "@/lib/blog";

type Props = {
  headings: BlogHeading[];
  label: string;
};

export function BlogTOC({ headings, label }: Props) {
  const [active, setActive] = useState<string | null>(headings[0]?.id ?? null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!headings.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: [0, 1] },
    );
    headings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [headings]);

  if (!headings.length) return null;

  return (
    <>
      {/* Mobile: collapsible */}
      <div className="lg:hidden mb-10 rounded-lg border border-rule bg-bg-subtle">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between px-5 py-4 font-mono text-xs uppercase tracking-[0.18em] text-ink-3"
        >
          <span>{label}</span>
          <span aria-hidden>{open ? "−" : "+"}</span>
        </button>
        {open && (
          <ol className="px-5 pb-5 space-y-2 text-sm text-ink-2">
            {headings
              .filter((h) => h.level === 2)
              .map((h, i) => (
                <li key={h.id}>
                  <a href={`#${h.id}`} className="hover:text-cyan-deep">
                    <span className="font-mono text-ink-4 mr-2">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {h.text}
                  </a>
                </li>
              ))}
          </ol>
        )}
      </div>

      {/* Desktop: sticky aside */}
      <aside className="hidden lg:block">
        <div className="sticky top-28">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-4 mb-4">
            {label}
          </p>
          <ol className="space-y-2.5 text-sm">
            {headings
              .filter((h) => h.level === 2)
              .map((h, i) => {
                const isActive = active === h.id;
                return (
                  <li key={h.id}>
                    <a
                      href={`#${h.id}`}
                      className={`block leading-snug transition-colors ${
                        isActive
                          ? "text-ink font-medium border-l-2 border-cyan-deep pl-3 -ml-[2px]"
                          : "text-ink-3 hover:text-ink pl-3"
                      }`}
                    >
                      <span className="font-mono text-[11px] text-ink-4 mr-1.5">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {h.text}
                    </a>
                  </li>
                );
              })}
          </ol>
        </div>
      </aside>
    </>
  );
}
